/**
 * Public Service Booking Page
 * ===========================
 * 
 * Customer-facing booking flow: select service -> pick time/staff -> enter details -> pay deposit -> confirmation
 */

import { useState, useEffect, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import {
  Calendar,
  Clock,
  User,
  Mail,
  Phone,
  ChevronLeft,
  ChevronRight,
  Check,
  ArrowLeft,
  ArrowRight,
  Loader2,
  Wrench,
  MessageCircle,
  Settings,
  Truck,
  Building,
  PoundSterling,
  CreditCard,
  Lock,
} from "lucide-react";

// Stripe test publishable key
const stripePromise = loadStripe("pk_live_51SkTY61WBmu3uIMLR5DWCr8Y79sPa9H4VeIkUfQM5pcrR88Gdf0q6UBqKmKMBcTOQKjnSYJ3DRmDdHfvKCBP2KDT00UEpMXP34");

const CATEGORY_ICONS = {
  repair: Wrench,
  consultation: MessageCircle,
  maintenance: Settings,
  delivery: Truck,
};

const CATEGORY_COLORS = {
  repair: "border-red-500/30 hover:border-red-500/50",
  consultation: "border-blue-500/30 hover:border-blue-500/50",
  maintenance: "border-green-500/30 hover:border-green-500/50",
  delivery: "border-amber-500/30 hover:border-amber-500/50",
};

// Card Element styling
const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: "#e2e8f0",
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSmoothing: "antialiased",
      fontSize: "16px",
      "::placeholder": {
        color: "#64748b"
      }
    },
    invalid: {
      color: "#ef4444",
      iconColor: "#ef4444"
    }
  }
};

// Payment Form Component
const PaymentForm = ({ bookingId, depositAmount, onSuccess, onBack }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);

  useEffect(() => {
    const createPaymentIntent = async () => {
      try {
        const res = await fetch(`${API}/bookings/public/create-payment-intent/${bookingId}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        });
        if (res.ok) {
          const data = await res.json();
          setClientSecret(data.client_secret);
        } else {
          const err = await res.json();
          setError(err.detail || "Failed to initialize payment");
        }
      } catch (e) {
        setError("Failed to connect to payment service");
      }
    };
    createPaymentIntent();
  }, [bookingId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || !clientSecret) return;

    setProcessing(true);
    setError(null);

    const cardElement = elements.getElement(CardElement);

    const { error: paymentError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
      payment_method: {
        card: cardElement,
      },
    });

    if (paymentError) {
      setError(paymentError.message);
      setProcessing(false);
      return;
    }

    if (paymentIntent.status === "succeeded") {
      // Confirm with backend
      const confirmRes = await fetch(`${API}/bookings/public/confirm-payment/${bookingId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ payment_intent_id: paymentIntent.id }),
      });

      if (confirmRes.ok) {
        const data = await confirmRes.json();
        toast.success("Payment successful!");
        onSuccess(data.booking);
      } else {
        setError("Payment processed but confirmation failed. Please contact support.");
      }
    }
    setProcessing(false);
  };

  const formatPrice = (pence) => `£${(pence / 100).toFixed(2)}`;

  return (
    <div className="max-w-md mx-auto">
      <h2 className="text-2xl font-bold text-center mb-2">Pay Deposit</h2>
      <p className="text-gray-500 text-center mb-8">
        Secure your booking with a {formatPrice(depositAmount)} deposit
      </p>

      {/* Payment Summary */}
      <div className="p-4 rounded-xl bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-500 border border-gray-200 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Lock className="h-4 w-4 text-emerald-400" />
          <span className="text-sm text-emerald-400">Secure Payment</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-gray-500">Deposit Amount</span>
          <span className="text-xl font-bold text-gray-900">{formatPrice(depositAmount)}</span>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="mb-6">
          <label className="text-sm text-gray-500 block mb-2">Card Details</label>
          <div className="p-4 rounded-xl bg-white border border-gray-200">
            {clientSecret ? (
              <CardElement options={CARD_ELEMENT_OPTIONS} />
            ) : (
              <div className="flex items-center justify-center py-2">
                <Loader2 className="h-5 w-5 animate-spin text-indigo-500" />
              </div>
            )}
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
            {error}
          </div>
        )}

        <div className="text-xs text-gray-500 mb-4 text-center">
          Test card: 4242 4242 4242 4242 | Any future date | Any CVC
        </div>

        <div className="flex gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={onBack}
            disabled={processing}
            className="flex-1 border-gray-200"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <Button
            type="submit"
            disabled={!stripe || !clientSecret || processing}
            className="flex-1 bg-indigo-600 hover:bg-indigo-700"
            data-testid="pay-deposit-btn"
          >
            {processing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <CreditCard className="h-4 w-4 mr-2" />
                Pay {formatPrice(depositAmount)}
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

const PublicBookingPage = () => {
  const { merchantSlug } = useParams();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  // Data state
  const [merchant, setMerchant] = useState(null);
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Booking flow state - 5 steps now
  const [step, setStep] = useState(1); // 1: Select Service, 2: Select Date/Time, 3: Enter Details, 4: Payment, 5: Confirmation
  const [selectedService, setSelectedService] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [availableSlots, setAvailableSlots] = useState({});
  const [loadingSlots, setLoadingSlots] = useState(false);
  
  // Customer form
  const [customerForm, setCustomerForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    phone: "",
    notes: "",
  });
  
  // Booking result
  const [bookingResult, setBookingResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Fetch merchant and services
  useEffect(() => {
    const fetchMerchantData = async () => {
      try {
        const res = await fetch(`${API}/services/public/${merchantSlug}`);
        if (res.ok) {
          const data = await res.json();
          setMerchant(data.merchant);
          setServices(data.services);
          setCategories(data.categories);
        } else if (res.status === 404) {
          setError("Merchant not found");
        } else {
          setError("Failed to load services");
        }
      } catch (err) {
        console.error("Error fetching merchant:", err);
        setError("Failed to load services");
      } finally {
        setLoading(false);
      }
    };
    
    fetchMerchantData();
  }, [merchantSlug]);

  // Auto-fill logged-in user details
  useEffect(() => {
    if (user) {
      setCustomerForm((prev) => ({
        ...prev,
        name: prev.name || user.name || "",
        email: prev.email || user.email || "",
      }));
    }
  }, [user]);

  // Generate dates for next 14 days
  const getAvailableDates = useCallback(() => {
    const dates = [];
    const today = new Date();
    
    for (let i = 0; i < 14; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      dates.push({
        date: date.toISOString().split("T")[0],
        dayName: date.toLocaleDateString("en-GB", { weekday: "short" }),
        dayNum: date.getDate(),
        month: date.toLocaleDateString("en-GB", { month: "short" }),
      });
    }
    
    return dates;
  }, []);

  const availableDates = getAvailableDates();

  // Fetch available slots
  const fetchSlots = async (date) => {
    if (!selectedService || !merchant) return;
    
    setLoadingSlots(true);
    try {
      const res = await fetch(
        `${API}/bookings/public/availability/${merchant.id}/${selectedService.service_id}?date=${date}`
      );
      
      if (res.ok) {
        const data = await res.json();
        setAvailableSlots(data.availability || {});
      }
    } catch (err) {
      console.error("Error fetching slots:", err);
    } finally {
      setLoadingSlots(false);
    }
  };

  useEffect(() => {
    if (selectedDate && selectedService) {
      fetchSlots(selectedDate);
      setSelectedTime(null);
      setSelectedStaff(null);
    }
  }, [selectedDate, selectedService]);

  // Handle service selection
  const selectService = (service) => {
    setSelectedService(service);
    setSelectedStaff(null);
    setSelectedDate(null);
    setSelectedTime(null);
    setAvailableSlots({});
    setStep(2);
  };

  // Handle time selection
  const selectTime = (staffId, time) => {
    setSelectedStaff(staffId);
    setSelectedTime(time);
    setStep(3);
  };

  // Submit booking (creates pending booking, then goes to payment)
  const submitBooking = async () => {
    if (!customerForm.name || !customerForm.email || !customerForm.phone) {
      toast.error("Please fill all required fields");
      return;
    }
    
    setSubmitting(true);
    
    try {
      const res = await fetch(`${API}/bookings/public/book/${merchant.id}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          service_id: selectedService.service_id,
          staff_id: selectedStaff,
          booking_date: selectedDate,
          start_time: selectedTime,
          customer_name: customerForm.name,
          customer_email: customerForm.email,
          customer_phone: customerForm.phone,
          notes: customerForm.notes,
        }),
      });
      
      if (res.ok) {
        const data = await res.json();
        setBookingResult(data);
        setStep(4); // Go to payment step
        toast.success("Booking created! Please complete payment.");
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to create booking");
      }
    } catch (err) {
      console.error("Error creating booking:", err);
      toast.error("Failed to create booking");
    } finally {
      setSubmitting(false);
    }
  };

  // Handle successful payment
  const handlePaymentSuccess = (booking) => {
    setBookingResult(booking);
    setStep(5);
  };

  // Reset booking flow
  const resetBooking = () => {
    setStep(1);
    setSelectedService(null);
    setSelectedStaff(null);
    setSelectedDate(null);
    setSelectedTime(null);
    setAvailableSlots({});
    setBookingResult(null);
    setCustomerForm({ name: user?.name || "", email: user?.email || "", phone: "", notes: "" });
  };

  const formatPrice = (pence) => `£${(pence / 100).toFixed(2)}`;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center text-gray-900">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-2">Oops!</h1>
          <p className="text-gray-500 mb-6">{error}</p>
          <Link to="/">
            <Button>Go Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white text-gray-900">
      {/* Header */}
      <header className="sticky top-0 z-50 glass-dark border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-indigo-500/20 flex items-center justify-center">
              <Building className="h-5 w-5 text-indigo-400" />
            </div>
            <div>
              <h1 className="font-bold">{merchant?.name}</h1>
              <p className="text-sm text-gray-500">Book a Service</p>
            </div>
          </div>
          
          {step > 1 && step < 5 && (
            <Button
              variant="ghost"
              onClick={() => setStep(step - 1)}
              className="text-gray-500"
              disabled={step === 4 && bookingResult}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          )}
        </div>
      </header>

      {/* Progress Steps */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="flex items-center justify-center gap-2 mb-8">
          {[1, 2, 3, 4, 5].map((s) => (
            <div key={s} className="flex items-center">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  s < step
                    ? "bg-emerald-500 text-gray-900"
                    : s === step
                    ? "bg-indigo-600 text-gray-900"
                    : "bg-gray-100 text-gray-500"
                }`}
              >
                {s < step ? <Check className="h-4 w-4" /> : s}
              </div>
              {s < 5 && (
                <div
                  className={`w-8 h-0.5 mx-1 ${
                    s < step ? "bg-emerald-500" : "bg-gray-100"
                  }`}
                />
              )}
            </div>
          ))}
        </div>

        {/* Step 1: Select Service */}
        {step === 1 && (
          <div>
            <h2 className="text-2xl font-bold text-center mb-2">Select a Service</h2>
            <p className="text-gray-500 text-center mb-8">Choose from our available services</p>
            
            <div className="grid sm:grid-cols-2 gap-4">
              {services.map((service) => {
                const Icon = CATEGORY_ICONS[service.category_id] || Settings;
                const colorClass = CATEGORY_COLORS[service.category_id] || "border-slate-500/30";
                
                return (
                  <button
                    key={service.service_id}
                    onClick={() => selectService(service)}
                    className={`text-left p-5 rounded-xl bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-500 border-2 transition-all hover:bg-white ${colorClass}`}
                    data-testid={`service-select-${service.service_id}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className="p-2 rounded-lg bg-white/5">
                        <Icon className="h-6 w-6 text-indigo-400" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg mb-1">{service.name}</h3>
                        <p className="text-sm text-gray-500 line-clamp-2 mb-3">{service.description}</p>
                        <div className="flex items-center gap-4 text-sm">
                          <span className="flex items-center gap-1 text-gray-500">
                            <Clock className="h-4 w-4" />
                            {service.duration_minutes}min
                          </span>
                          <span className="flex items-center gap-1 text-indigo-400 font-semibold">
                            <PoundSterling className="h-4 w-4" />
                            {formatPrice(service.base_price)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
            
            {services.length === 0 && (
              <div className="text-center py-12">
                <Settings className="h-12 w-12 mx-auto mb-4 text-slate-600" />
                <p className="text-gray-500">No services available at the moment</p>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Select Date & Time */}
        {step === 2 && selectedService && (
          <div>
            <h2 className="text-2xl font-bold text-center mb-2">Select Date & Time</h2>
            <p className="text-gray-500 text-center mb-8">
              {selectedService.name} - {selectedService.duration_minutes}min
            </p>
            
            {/* Date Picker */}
            <div className="mb-8">
              <h3 className="text-sm text-gray-500 mb-3">Choose a date</h3>
              <div className="flex gap-2 overflow-x-auto pb-2">
                {availableDates.map((d) => (
                  <button
                    key={d.date}
                    onClick={() => setSelectedDate(d.date)}
                    className={`flex-shrink-0 w-16 py-3 rounded-xl border text-center transition-all ${
                      selectedDate === d.date
                        ? "bg-indigo-600 border-indigo-500 text-gray-900"
                        : "bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-500 border-gray-200 hover:border-indigo-500/50"
                    }`}
                  >
                    <p className="text-xs text-gray-500">{d.dayName}</p>
                    <p className="text-lg font-bold">{d.dayNum}</p>
                    <p className="text-xs">{d.month}</p>
                  </button>
                ))}
              </div>
            </div>
            
            {/* Time Slots */}
            {selectedDate && (
              <div>
                <h3 className="text-sm text-gray-500 mb-3">Available times</h3>
                
                {loadingSlots ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-indigo-500" />
                  </div>
                ) : Object.keys(availableSlots).length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Calendar className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No available slots on this date</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {Object.entries(availableSlots).map(([staffId, data]) => (
                      <div key={staffId} className="p-4 rounded-xl bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-500 border border-gray-200">
                        <div className="flex items-center gap-2 mb-3">
                          <User className="h-4 w-4 text-gray-500" />
                          <span className="font-medium">{data.staff?.name || "Staff"}</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {data.slots.length === 0 ? (
                            <p className="text-sm text-gray-500">No slots available</p>
                          ) : (
                            data.slots.map((slot) => (
                              <button
                                key={slot}
                                onClick={() => selectTime(staffId, slot)}
                                className={`px-4 py-2 rounded-lg border text-sm transition-all ${
                                  selectedStaff === staffId && selectedTime === slot
                                    ? "bg-indigo-600 border-indigo-500"
                                    : "bg-gray-100 border-gray-200 hover:border-indigo-500/50"
                                }`}
                              >
                                {slot}
                              </button>
                            ))
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Step 3: Enter Details */}
        {step === 3 && (
          <div className="max-w-md mx-auto">
            <h2 className="text-2xl font-bold text-center mb-2">Your Details</h2>
            <p className="text-gray-500 text-center mb-8">Enter your contact information</p>
            
            {/* Booking Summary */}
            <div className="p-4 rounded-xl bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-500 border border-gray-200 mb-6">
              <h3 className="font-semibold mb-3">{selectedService?.name}</h3>
              <div className="flex items-center gap-4 text-sm text-gray-500">
                <span className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  {new Date(selectedDate).toLocaleDateString("en-GB", {
                    weekday: "long",
                    day: "numeric",
                    month: "long",
                  })}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  {selectedTime}
                </span>
              </div>
            </div>
            
            {/* Form */}
            <div className="space-y-4">
              <div>
                <label className="text-sm text-gray-500 block mb-2">Full Name *</label>
                <Input
                  value={customerForm.name}
                  onChange={(e) => setCustomerForm({ ...customerForm, name: e.target.value })}
                  placeholder="John Smith"
                  className="bg-white border-gray-200"
                  data-testid="public-booking-name"
                />
              </div>
              
              <div>
                <label className="text-sm text-gray-500 block mb-2">Email *</label>
                <Input
                  type="email"
                  value={customerForm.email}
                  onChange={(e) => setCustomerForm({ ...customerForm, email: e.target.value })}
                  placeholder="john@example.com"
                  className="bg-white border-gray-200"
                />
              </div>
              
              <div>
                <label className="text-sm text-gray-500 block mb-2">Phone *</label>
                <Input
                  type="tel"
                  value={customerForm.phone}
                  onChange={(e) => setCustomerForm({ ...customerForm, phone: e.target.value })}
                  placeholder="+44 7700 000000"
                  className="bg-white border-gray-200"
                />
              </div>
              
              <div>
                <label className="text-sm text-gray-500 block mb-2">Notes (optional)</label>
                <Input
                  value={customerForm.notes}
                  onChange={(e) => setCustomerForm({ ...customerForm, notes: e.target.value })}
                  placeholder="Any special requests..."
                  className="bg-white border-gray-200"
                />
              </div>
            </div>
            
            {/* Deposit Info */}
            <div className="mt-6 p-4 rounded-xl bg-amber-500/10 border border-amber-500/20">
              <div className="flex items-center gap-2 mb-2">
                <PoundSterling className="h-4 w-4 text-amber-400" />
                <span className="font-medium text-amber-400">Deposit Required</span>
              </div>
              <p className="text-sm text-gray-500">
                A {selectedService?.deposit_percent}% deposit ({formatPrice(selectedService?.base_price * selectedService?.deposit_percent / 100)}) 
                will be required to confirm your booking.
              </p>
            </div>
            
            <Button
              onClick={submitBooking}
              disabled={submitting}
              className="w-full mt-6 h-12 bg-indigo-600 hover:bg-indigo-700"
              data-testid="confirm-booking-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating Booking...
                </>
              ) : (
                <>
                  Continue to Payment
                  <ArrowRight className="h-4 w-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        )}

        {/* Step 4: Payment */}
        {step === 4 && bookingResult && (
          <Elements stripe={stripePromise}>
            <PaymentForm
              bookingId={bookingResult.booking_id}
              depositAmount={bookingResult.deposit_amount}
              onSuccess={handlePaymentSuccess}
              onBack={() => setStep(3)}
            />
          </Elements>
        )}

        {/* Step 5: Confirmation */}
        {step === 5 && bookingResult && (
          <div className="max-w-md mx-auto text-center">
            <div className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-6">
              <Check className="h-10 w-10 text-emerald-500" />
            </div>
            
            <h2 className="text-2xl font-bold mb-2">Booking Confirmed!</h2>
            <p className="text-gray-500 mb-6">
              Your booking reference is <span className="text-gray-900 font-mono">{bookingResult.booking_number}</span>
            </p>
            
            <div className="p-4 rounded-xl bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-500 border border-gray-200 mb-6 text-left">
              <h3 className="font-semibold mb-3">{bookingResult.service_name || selectedService?.name}</h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-gray-500">
                  <Calendar className="h-4 w-4" />
                  <span>
                    {new Date(bookingResult.booking_date || selectedDate).toLocaleDateString("en-GB", {
                      weekday: "long",
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-gray-500">
                  <Clock className="h-4 w-4" />
                  <span>{bookingResult.start_time || selectedTime}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-500">
                  <User className="h-4 w-4" />
                  <span>{bookingResult.staff_name}</span>
                </div>
              </div>
              
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Deposit Paid</span>
                  <span className="text-emerald-400">{formatPrice(bookingResult.deposit_amount)}</span>
                </div>
                <div className="flex justify-between text-sm mt-1">
                  <span className="text-gray-500">Remaining Balance</span>
                  <span className="text-amber-400">{formatPrice(bookingResult.final_amount)}</span>
                </div>
                <div className="flex justify-between font-medium mt-2 pt-2 border-t border-gray-200">
                  <span>Total</span>
                  <span>{formatPrice(bookingResult.total_amount)}</span>
                </div>
              </div>
            </div>
            
            <p className="text-sm text-gray-500 mb-6">
              A confirmation email has been sent to <span className="text-gray-900">{customerForm.email}</span>
            </p>
            
            <div className="flex gap-3 justify-center">
              <Button variant="outline" onClick={resetBooking} className="border-gray-200">
                Book Another
              </Button>
              <Link to="/">
                <Button className="bg-indigo-600 hover:bg-indigo-700">
                  Go Home
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicBookingPage;
